/**
 * Audit log real contra `/api/audit`.
 *
 * Endpoint admin-only. Devuelve filas de cm_audit_log con hash chain
 * verificable. La cadena se valida via `/api/audit/verify` (operación
 * costosa — sólo on-demand).
 */
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";
import type { ApiAuditEntry } from "@/lib/adapters";
import type { ClientSlug } from "@/types";

export interface AuditFilters {
  clientSlug?: ClientSlug;
  action?: string;
  resourceType?: string;
  resourceId?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
}

export interface PanelAuditEntry {
  id: string;
  prevHash: string | null;
  hash: string;
  actorId: string;
  actorRole: "admin" | "operator" | "client" | "external" | "system";
  action: string;
  resourceType: string;
  resourceId: string;
  payload: Record<string, unknown>;
  clientSlug: ClientSlug | null;
  createdAt: string;
}

function apiToPanel(a: ApiAuditEntry): PanelAuditEntry {
  return {
    id: String(a.id),
    prevHash: a.prevHash,
    hash: a.hash,
    actorId: a.actorId,
    actorRole: a.actorRole,
    action: a.action,
    resourceType: a.resourceType,
    resourceId: a.resourceId,
    payload: a.payload ?? {},
    clientSlug: (a.clientSlug as ClientSlug | null) ?? null,
    createdAt: a.createdAt,
  };
}

function buildAuditQS(filters: AuditFilters, beforeId?: string): string {
  const qs = new URLSearchParams();
  if (filters.clientSlug) qs.set("clientSlug", filters.clientSlug);
  if (filters.action) qs.set("action", filters.action);
  if (filters.resourceType) qs.set("resourceType", filters.resourceType);
  if (filters.resourceId) qs.set("resourceId", filters.resourceId);
  if (filters.fromDate) qs.set("fromDate", filters.fromDate);
  if (filters.toDate) qs.set("toDate", filters.toDate);
  qs.set("limit", String(filters.limit ?? 100));
  if (beforeId) qs.set("beforeId", beforeId);
  return qs.toString();
}

/**
 * Paginación cursor-based — el front llama fetchNextPage() para traer más
 * filas, infinite-style. El primer fetch trae las últimas N; cada siguiente
 * trae las N anteriores con beforeId = id más bajo de la página actual.
 */
export function useAuditLog(filters: AuditFilters = {}) {
  return useInfiniteQuery<
    { items: PanelAuditEntry[]; nextCursor: string | null },
    Error
  >({
    queryKey: ["audit", filters],
    refetchInterval: 60_000,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    queryFn: async ({ pageParam }) => {
      const res = await apiGet<{
        items: ApiAuditEntry[];
        nextCursor: string | null;
      }>(`/audit?${buildAuditQS(filters, pageParam as string | undefined)}`);
      return {
        items: res.items.map(apiToPanel),
        nextCursor: res.nextCursor,
      };
    },
  });
}

export interface AuditChainVerifyResult {
  valid: boolean;
  totalChecked: number;
  brokenAtId: string | null;
  brokenReason: string | null;
}

export function useAuditChainVerify(enabled = false) {
  return useQuery<AuditChainVerifyResult>({
    queryKey: ["audit", "verify"],
    enabled,
    queryFn: async () => apiGet<AuditChainVerifyResult>("/audit/verify"),
  });
}

/**
 * Devuelve la URL absoluta para descargar el CSV con los mismos filtros que
 * la página. Se asigna a un `<a download>` o `window.open()` — no se puede
 * fetch() porque queremos que el browser maneje el download.
 */
export function buildAuditCsvUrl(filters: AuditFilters): string {
  const qs = new URLSearchParams();
  if (filters.clientSlug) qs.set("clientSlug", filters.clientSlug);
  if (filters.action) qs.set("action", filters.action);
  if (filters.resourceType) qs.set("resourceType", filters.resourceType);
  if (filters.resourceId) qs.set("resourceId", filters.resourceId);
  if (filters.fromDate) qs.set("fromDate", filters.fromDate);
  if (filters.toDate) qs.set("toDate", filters.toDate);
  // El export ahora streamea TODO con iterateAuditLog (no usa limit). No
  // pasamos limit en la URL para evitar confusión.
  return `/api/audit/export.csv?${qs.toString()}`;
}
